import { UseMutateAsyncFunction, useMutation } from '@tanstack/react-query'
import { buildURL, stateStorage } from './utils'

interface IOauthSenderProps<P extends Record<string, unknown>, S extends Record<string, unknown>> {
  // The full url to the authorize endpoint
  authorizeUrl: string
  // Your OAuth client id
  clientId: string
  // The URL where the provider should redirect your users back
  redirectUri: string
  // Additional state to get back from the service provider.
  // This state will be sent back by the OAuth-provider once the process is done.
  // This state can for example then be used to redirect the user back to where they
  // started the auth process.
  state: S
  // Function to call and generate random state string. If that
  // function is not available we will simple generate an unique string with
  // uuid
  stateGenerator?: () => Promise<string> | string
  // Extra query parameters to send to service provider, ex. scope.
  extraParams?: P
}

interface IUseOauthSenderResult {
  isLoading: boolean
  error: any
  url: string | undefined
  generateUrl: UseMutateAsyncFunction<string>
}

/**
 * Hook that should be used to simplify the Authorization Code Flow: https://auth0.com/docs/flows/concepts/auth-code
 * ! - BE AWARE - ! Make sure it applies to your use case
 *
 * Normally this flow is used for server-side applications. Thus, we need an extra layer of security
 * to validate the state sent to the OAuth tenant and we need to make sure the call to extract the
 * token is enhanced in our backend. By enhanced here we mean that we cannot add the client secret
 * in the frontend, so we should at least have this level of indirection to make sure we don't expose
 * any information we shouldn't. Also, the token should be stored in the backend to respect security concerns
 *
 * @param senderProps - See the {@link IOauthSenderProps} interface for more details
 */
export function useOauthSender<P extends Record<string, any>, S extends Record<string, any> = Record<string, unknown>>({
  authorizeUrl,
  clientId,
  redirectUri,
  state,
  stateGenerator,
  extraParams
}: IOauthSenderProps<P, S>): IUseOauthSenderResult {
  const {
    isPending: isLoading,
    error,
    data: url,
    mutateAsync
  } = useMutation({
    mutationFn: async () => {
      const nonce = await stateStorage.saveState<S>(state, stateGenerator)
      return buildURL(authorizeUrl, {
        client_id: clientId,
        redirect_uri: redirectUri,
        response_type: 'code',
        state: nonce,
        ...(extraParams || {})
      })
    }
  })
  return { isLoading, error, url, generateUrl: mutateAsync }
}

useOauthSender.defaultProps = {
  state: null,
  extraParams: null,
  render: null,
  component: null,
  children: null
}

export default useOauthSender
